import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBillingCompanyContext } from "../../../providers";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import {
  fetchServiceItemListAsync,
  IServiceItem,
} from "../../../redux/service";
import { IUpsellingDataItem } from "../types/upsell.types";
import {
  DEFAULT_BOOKING_PERIOD,
  DEFAULT_DATE_RANGE,
  API_PAGINATION_DEFAULTS,
} from "../constants/dashboardConstants";
import { extractUniqueUpsellData } from "../helpers/extractUpsellData";
import {
  fetchUpsellInsightAsync,
  autoGenerateCampaignAsync,
  IUpsellInsightData,
} from "../../../redux/upsellLead";

interface ICampaignGenerationData {
  type: "bulk";
  selectedUuids: string[];
  leadsCount: number;
  selectedServiceCategory: any;
  selectedVenueCategory: any;
  selectedFoodCategory: any;
}

export const useUpsellDashboard = () => {
  const companyOutlet = useBillingCompanyContext();
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  // State
  const [dashboardData, setDashboardData] = useState<
    IUpsellingDataItem[] | null
  >(null);

  const [showUnableUpsell, setShowUnableUpsell] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [selectedBookingPeriod, setSelectedBookingPeriod] = useState<number>(
    DEFAULT_BOOKING_PERIOD,
  );
  const [selectedServiceCategory, setSelectedServiceCategory] =
    useState<any>(null);
  const [selectedVenueCategory, setSelectedVenueCategory] = useState<any>(null);
  const [selectedFoodCategory, setSelectedFoodCategory] = useState<any>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [campaignGenerationData, setCampaignGenerationData] =
    useState<ICampaignGenerationData | null>(null);
  const [campaignLoading, setCampaignLoading] = useState(false);

  // Derived data - Filter only service categories from the mixed data
  const serviceCardsData = useMemo(
    () =>
      dashboardData?.filter((item: any) => "service_category_uuid" in item) ||
      [],
    [dashboardData],
  );

  // Filter only venues from the mixed data
  const venuesCardsData = useMemo(
    () => dashboardData?.filter((item: any) => "venue_uuid" in item) || [],
    [dashboardData],
  );

  // Filter only venues from the mixed data
  const foodCardsData = useMemo(
    () => dashboardData?.filter((item: any) => "food_item_uuid" in item) || [],
    [dashboardData],
  );

  // Pre-process service data and sort by leads count
  const serviceCardsWithUpsells = useMemo(() => {
    return serviceCardsData
      .filter((item: any) => item.leads && item.leads.length > 0)
      .map((item: any) => {
        const leads = item.leads || [];
        const uniqueUpsells = extractUniqueUpsellData(leads);
        return {
          ...item,
          uniqueUpsells,
        };
      })
      .sort((a, b) => {
        const aLeadsCount = a.leads?.length || 0;
        const bLeadsCount = b.leads?.length || 0;

        if (aLeadsCount > 0 && bLeadsCount === 0) return -1;
        if (aLeadsCount === 0 && bLeadsCount > 0) return 1;
        return bLeadsCount - aLeadsCount;
      });
  }, [serviceCardsData]);

  // Pre-process venue data and sort by leads count
  const venueCardsWithUpsells = useMemo(() => {
    return venuesCardsData
      .filter((item: any) => item.leads && item.leads.length > 0)
      .map((item: any) => {
        const leads = item.leads || [];
        const uniqueUpsells = extractUniqueUpsellData(leads);
        return {
          ...item,
          uniqueUpsells,
        };
      })
      .sort((a, b) => {
        const aLeadsCount = a.leads?.length || 0;
        const bLeadsCount = b.leads?.length || 0;

        if (aLeadsCount > 0 && bLeadsCount === 0) return -1;
        if (aLeadsCount === 0 && bLeadsCount > 0) return 1;
        return bLeadsCount - aLeadsCount;
      });
  }, [venuesCardsData]);

  // Pre-process venue data and sort by leads count
  const foodCardsWithUpsells = useMemo(() => {
    return foodCardsData
      .filter((item: any) => item.leads && item.leads.length > 0)
      .map((item: any) => {
        const leads = item.leads || [];
        const uniqueUpsells = extractUniqueUpsellData(leads);
        return {
          ...item,
          uniqueUpsells,
        };
      })
      .sort((a, b) => {
        const aLeadsCount = a.leads?.length || 0;
        const bLeadsCount = b.leads?.length || 0;

        if (aLeadsCount > 0 && bLeadsCount === 0) return -1;
        if (aLeadsCount === 0 && bLeadsCount > 0) return 1;
        return bLeadsCount - aLeadsCount;
      });
  }, [foodCardsData]);

  // Fetch upselling insights from API using redux action
  const fetchUpsellingInsights = useCallback(
    async (bookingEndPeriod: number) => {
      dispatch(
        fetchServiceItemListAsync(
          API_PAGINATION_DEFAULTS.pageNumber,
          API_PAGINATION_DEFAULTS.pageSize,
          API_PAGINATION_DEFAULTS.rowsPerPage,
          API_PAGINATION_DEFAULTS.status,
          DEFAULT_DATE_RANGE,
          companyOutlet.comapny.company_outlet_uuid,
          null,
          [],
          "",
          "",
          async (isSuccess: boolean, data: IServiceItem[]) => {
            if (isSuccess && data.length > 0) {
              const payload = {
                leads_uuid: "",
                booking_start_period: 1,
                booking_end_period: bookingEndPeriod,
                value: null,
                company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
                status: "ACTIVE",
              };

              dispatch(
                fetchUpsellInsightAsync(
                  payload,
                  (
                    isInsightSuccess: boolean,
                    insightData?: IUpsellInsightData[],
                  ) => {
                    if (isInsightSuccess && insightData) {
                      setDashboardData(insightData as IUpsellingDataItem[]);
                      setShowWarning(false);
                    } else {
                      // Check if it's the specific "no booked leads" warning
                      setDashboardData([]);
                    }
                  },
                ),
              );
            } else {
              setShowUnableUpsell(true);
            }
          },
        ),
      );
    },
    [companyOutlet.comapny.company_outlet_uuid, dispatch],
  );

  // Handle booking period card selection
  const handleBookingPeriodSelect = (bookingPeriod: number) => {
    setSelectedBookingPeriod(bookingPeriod);
  };

  // Confirm and execute campaign generation using redux action
  const confirmCampaignGeneration = useCallback(async () => {
    if (!campaignGenerationData) return;

    setConfirmDialogOpen(false);
    setCampaignLoading(true);

    const payload = {
      refernce_column_name_value: campaignGenerationData.selectedUuids,
      company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      booking_end_period: selectedBookingPeriod,
      status: "ACTIVE",
    };

    dispatch(
      autoGenerateCampaignAsync(payload, (isSuccess, data) => {
        setCampaignLoading(false);
        setCampaignGenerationData(null);

        if (isSuccess && data?.market_campaign_uuid) {
          // navigate(`/market-campaign/manage/${data.market_campaign_uuid}`);
          navigate(`/market-campaign`);
          return true;
        }
        return false;
      }),
    );

    return false;
  }, [
    campaignGenerationData,
    companyOutlet.comapny.company_outlet_uuid,
    dispatch,
    navigate,
    selectedBookingPeriod,
  ]);

  // Initialize campaign generation
  const initiateCampaignGeneration = useCallback(
    (selectedUuids: string[], leadsCount: number) => {
      if (leadsCount === 0) {
        dispatch(
          showMessage({
            type: "warning",
            message: "Please select at least one card to generate campaign",
            displayAs: "snackbar",
          }),
        );
        return;
      }

      setCampaignGenerationData({
        type: "bulk",
        selectedUuids,
        leadsCount,
        selectedServiceCategory,
        selectedVenueCategory,
        selectedFoodCategory,
      });
      setConfirmDialogOpen(true);
    },
    [dispatch],
  );

  // Initialize with default data
  useEffect(() => {
    fetchUpsellingInsights(selectedBookingPeriod);
  }, [fetchUpsellingInsights, selectedBookingPeriod]);

  return {
    // State
    dashboardData,
    showUnableUpsell,
    showWarning,
    selectedBookingPeriod,
    selectedServiceCategory,
    selectedVenueCategory,
    selectedFoodCategory,
    confirmDialogOpen,
    campaignGenerationData,
    campaignLoading,

    // Derived data
    serviceCardsWithUpsells,
    venueCardsWithUpsells,
    foodCardsWithUpsells,

    // Setters
    setSelectedServiceCategory,
    setSelectedVenueCategory,
    setSelectedFoodCategory,
    setConfirmDialogOpen,

    // Actions
    handleBookingPeriodSelect,
    confirmCampaignGeneration,
    initiateCampaignGeneration,
    navigate,
  };
};
